import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

// The (!) beside a field. Ali, 2026-08-16.
//
// THE STANDARD IS THE COMPONENT, not a guideline someone has to remember. Every hint answers three
// things in this order, and it takes three separate keys so it CANNOT skip one:
//
//   what     — what this number is, in one line, no jargon
//   affects  — does changing it move money the USER pays, or only what the admin sees? This is the
//              question that made اقتصاد بارگذاری پیکره unreadable: five fields, and no way to tell
//              that two of them charge nobody anything.
//   example  — a real number. "2 MB → 234 credits" landed where "credits per 1000 tokens" did not.
//
// A field whose hint has nothing to say for `affects` is a field that probably does not need a hint.
//
// Each part is its own locale key, so all three are editable from «متن‌های برنامه» like every other
// phrase — the wording of an explanation is exactly the kind of thing that needs fixing after you
// watch someone misread it.

export default function InfoHint({ base, testId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Click-away and Escape. Hover alone is not enough: on a touch screen there is no hover, and the
  // text is long enough that a tooltip which vanishes on the way to reading it is useless.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const what = t(`${base}.what`);
  const affects = t(`${base}.affects`);
  const example = t(`${base}.example`);

  // Ali, 2026-08-16: "when i hover out of icon it vanish, but when i keep on icon or box, it stays
  // there." The first version put the open on the ICON and the close on the PANEL, so moving the
  // mouse off the icon without crossing the panel left it stranded — and hovering a second icon
  // then showed two panels at once.
  //
  // Both handlers now sit on the WRAPPER, which contains the icon AND the panel. Leaving the
  // wrapper means leaving both, and moving between icon and panel never leaves it, so the panel
  // stays put while you read it. A click still toggles, because a touch screen has no hover.
  return (
    <span
      className="relative inline-flex"
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-muted-foreground hover:text-foreground align-middle"
        aria-expanded={open}
        data-testid={testId || `info-${base}`}
      >
        <Info className="h-3.5 w-3.5" />
      </button>

      {open && (
        <span
          role="tooltip"
          className="absolute z-50 top-6 start-0 w-72 rounded-md border border-border bg-card p-3 shadow-lg text-start"
          data-testid={`info-panel-${base}`}
        >
          <span className="block text-xs text-foreground">{what}</span>
          {/* The money line is visually separated on purpose: it is the part people miss, and the
              part that costs them when they miss it. */}
          <span className="block text-xs text-warning mt-2">{affects}</span>
          <span className="block text-xs text-muted-foreground mt-2 font-data">{example}</span>
        </span>
      )}
    </span>
  );
}
